import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  Grid, TextField, Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import UploadLogoButton from './UploadLogoButton';
import { getPersistedLogoUrl } from '../../../utils/logoPreview';

const API_URL = process.env.REACT_APP_API_URL;

const emptyConfig = {
  nombre_empresa: '', direccion: '', telefono: '', nit: '', pie_entrada: '',
  pie_salida: '', pie_mensualidad: '', pie_mensualidad_renovacion: '', logo: null,
};

const ConfigDialog = ({ open, handleClose, configData, onSave }) => {
  const [formData, setFormData] = useState(emptyConfig);
  const [selectedLogo, setSelectedLogo] = useState(null);
  const [localPreview, setLocalPreview] = useState(null);
  const localPreviewRef = useRef(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [persistedLogo, setPersistedLogo] = useState(null);
  const [persistedVersion, setPersistedVersion] = useState(null);
  const [imageFailed, setImageFailed] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const wasOpenRef = useRef(false);

  const revokeLocalPreview = useCallback(() => {
    if (localPreviewRef.current) {
      URL.revokeObjectURL(localPreviewRef.current);
      localPreviewRef.current = null;
    }
    setLocalPreview(null);
  }, []);

  useEffect(() => {
    if (open && !wasOpenRef.current && configData) {
      setFormData({ ...configData, logo: null });
      setSelectedLogo(null);
      setPersistedLogo(configData.logo || null);
      setPersistedVersion(configData.ultima_modificacion || null);
      setImageFailed(false);
      setError('');
    } else if (!open && wasOpenRef.current) {
      revokeLocalPreview();
      setSelectedLogo(null);
      setPreviewOpen(false);
      setImageFailed(false);
      setError('');
    }
    wasOpenRef.current = open;
  }, [configData, open, revokeLocalPreview]);

  useEffect(() => {
    return () => {
      if (localPreviewRef.current) URL.revokeObjectURL(localPreviewRef.current);
    };
  }, []);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((previous) => ({ ...previous, [name]: value }));
  };

  const handleFileSelected = (file) => {
    revokeLocalPreview();
    try {
      const objectUrl = URL.createObjectURL(file);
      localPreviewRef.current = objectUrl;
      setLocalPreview(objectUrl);
      setSelectedLogo(file);
      setImageFailed(false);
      setError('');
    } catch (previewError) {
      setSelectedLogo(null);
      setImageFailed(false);
      setError('No se pudo preparar la previsualización del archivo seleccionado.');
    }
  };

  const resetTemporaryPreview = useCallback(() => {
    revokeLocalPreview();
    setSelectedLogo(null);
    setImageFailed(false);
    setPreviewOpen(false);
    setError('');
  }, [revokeLocalPreview]);

  const handleDialogClose = () => {
    if (saving) return;
    resetTemporaryPreview();
    handleClose();
  };

  const handleSubmit = async () => {
    if (saving) return;
    const data = new FormData();
    Object.entries(formData).forEach(([key, value]) => {
      if (key !== 'logo' && value !== null) data.append(key, value);
    });
    if (selectedLogo) data.append('logo', selectedLogo);

    setSaving(true);
    setError('');
    try {
      const savedConfig = await onSave(data);
      setPersistedLogo(savedConfig?.logo || null);
      setPersistedVersion(savedConfig?.ultima_modificacion || null);
      resetTemporaryPreview();
      handleClose();
    } catch (saveError) {
      setError(saveError.message || 'No se pudo guardar la configuración.');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveLogo = async () => {
    if (saving || !window.confirm('¿Eliminar el logo configurado?')) return;
    const data = new FormData();
    data.append('eliminar_logo', 'true');
    setSaving(true);
    setError('');
    try {
      const savedConfig = await onSave(data);
      revokeLocalPreview();
      setSelectedLogo(null);
      setPersistedLogo(savedConfig?.logo || null);
      setPersistedVersion(savedConfig?.ultima_modificacion || null);
      setImageFailed(false);
      setPreviewOpen(false);
    } catch (removeError) {
      setError(removeError.message || 'No se pudo eliminar el logo.');
    } finally {
      setSaving(false);
    }
  };

  const persistedUrl = getPersistedLogoUrl({
    logo: persistedLogo,
    ultimaModificacion: persistedVersion,
    apiUrl: API_URL,
  });
  const logoUrl = localPreview || persistedUrl;
  const previewType = localPreview ? 'temporal' : 'persistida';
  const handleImageError = () => {
    if (imageFailed) return;
    setImageFailed(true);
    setPreviewOpen(false);
    setError(previewType === 'temporal'
      ? 'No se pudo cargar la previsualización del archivo seleccionado.'
      : 'No se pudo cargar la previsualización del logo guardado.');
  };

  return (
    <>
      <Dialog open={open} onClose={saving ? undefined : handleDialogClose} fullWidth maxWidth="md">
        <DialogTitle>Configuración del Sistema</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}><TextField fullWidth label="Nombre de la Empresa" name="nombre_empresa" value={formData.nombre_empresa} onChange={handleChange} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth label="Dirección" name="direccion" value={formData.direccion} onChange={handleChange} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth label="Teléfono" name="telefono" value={formData.telefono} onChange={handleChange} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth label="NIT" name="nit" value={formData.nit} onChange={handleChange} /></Grid>
            <Grid item xs={12}><TextField fullWidth multiline rows={2} label="Pie Entrada" name="pie_entrada" value={formData.pie_entrada} onChange={handleChange} /></Grid>
            <Grid item xs={12}><TextField fullWidth multiline rows={2} label="Pie Salida" name="pie_salida" value={formData.pie_salida} onChange={handleChange} /></Grid>
            <Grid item xs={12}><TextField fullWidth multiline rows={2} label="Pie Mensualidad" name="pie_mensualidad" value={formData.pie_mensualidad} onChange={handleChange} /></Grid>
            <Grid item xs={12}><TextField fullWidth multiline rows={2} label="Pie Renovación" name="pie_mensualidad_renovacion" value={formData.pie_mensualidad_renovacion} onChange={handleChange} /></Grid>
            <Grid item xs={12}>
              {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
              {logoUrl ? (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
                  {!imageFailed ? (
                    <Box component="img" src={logoUrl} alt="Logo actual de la empresa" onClick={() => setPreviewOpen(true)} onError={handleImageError} sx={{ width: 120, height: 80, objectFit: 'contain', border: '1px solid #ccc', borderRadius: 1, cursor: 'pointer' }} />
                  ) : (
                    <Typography color="text.secondary">Previsualización no disponible</Typography>
                  )}
                  <UploadLogoButton onFileSelected={handleFileSelected} onError={setError} label="Editar/Reemplazar" disabled={saving} />
                  <Button color="error" variant="outlined" startIcon={<DeleteIcon />} onClick={handleRemoveLogo} disabled={saving}>Eliminar</Button>
                </Box>
              ) : (
                <Box>
                  <Typography color="text.secondary" sx={{ mb: 1 }}>Sin logo configurado</Typography>
                  <UploadLogoButton onFileSelected={handleFileSelected} onError={setError} disabled={saving} />
                </Box>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose} color="inherit" disabled={saving}>Cancelar</Button>
          <Button onClick={handleSubmit} variant="contained" disabled={saving}>{saving ? 'Guardando...' : 'Guardar'}</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="lg">
        <DialogTitle>Logo actual</DialogTitle>
        <DialogContent>
          {logoUrl && !imageFailed && <Box component="img" src={logoUrl} alt="Vista ampliada del logo de la empresa" onError={handleImageError} sx={{ display: 'block', maxWidth: '85vw', maxHeight: '75vh', objectFit: 'contain' }} />}
        </DialogContent>
        <DialogActions><Button onClick={() => setPreviewOpen(false)}>Cerrar</Button></DialogActions>
      </Dialog>
    </>
  );
};

export default ConfigDialog;
